import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import FlameIndicator from '../FlameIndicator';
import { AnimatedBadge } from '../AnimatedBadge';
import { titleForLevel } from '../../utils/levelTitles';
import {
  Trophy, Compass, MessageSquare, ThumbsUp, Heart, Lightbulb, Calendar,
  Flame, Target, Anchor, Sparkles, Star, Award, TrendingUp, Zap,
} from 'lucide-react';

const TIMELINE_ICONS = {
  sparkles: Sparkles, compass: Compass, trophy: Trophy, target: Target, anchor: Anchor,
};

const relTime = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `il y a ${Math.floor(diff / 86400)} j`;
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
};

/* ============================================================
 * 1. Progression Card — big XP bar + title + streak
 * ============================================================ */
const ProgressionCard = ({ user, levelInfo, streak }) => {
  if (!user) return null;
  const currentTitle = titleForLevel(user.level || 1);
  const nextTitle = titleForLevel((user.level || 1) + 1);
  const percent = levelInfo?.progress_percent ?? 0;

  return (
    <Card className="card-base overflow-hidden relative border-primary/20">
      <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-primary/10 to-transparent rounded-bl-full" />
      <CardHeader className="pb-2 relative">
        <CardTitle className="text-lg flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" />
          Ma progression
        </CardTitle>
      </CardHeader>
      <CardContent className="relative space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs uppercase tracking-widest text-primary font-bold">
            {currentTitle}
          </span>
          <span className="text-xs text-muted-foreground">Niveau {user.level || 1}</span>
          <FlameIndicator variant="inline" streak={streak} />
        </div>

        <div>
          <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
            <span className="flex items-center gap-1">
              <Zap className="w-3 h-3 text-primary" /> {user.points || 0} XP
            </span>
            {levelInfo?.xp_next_level ? (
              <span>{levelInfo.xp_next_level - (user.points || 0)} XP → {nextTitle}</span>
            ) : (
              <span>🏆 Niveau max atteint</span>
            )}
          </div>
          <Progress value={percent} className="h-3" />
          <p className="text-[10px] text-muted-foreground mt-1 text-right">{percent}%</p>
        </div>

        {streak && streak.current_streak >= 1 && (
          <div className="pt-2 border-t border-border/50">
            <p className="text-xs text-muted-foreground">
              🔥 <b>{streak.current_streak} jours</b> d&apos;affilée
              {streak.eligible && (
                <span className="text-green-600 font-semibold"> · Multiplicateur ×{streak.xp_mult} XP actif</span>
              )}
              {streak.next_tier_days && (
                <span> · +{streak.next_tier_days} j pour le prochain palier</span>
              )}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ============================================================
 * 2. Stats Squares — 4 chiffrés
 * ============================================================ */
const StatsSquares = ({ stats, streak }) => {
  if (!stats) return null;
  const items = [
    { icon: Compass, label: 'Chapitres', value: `${stats.chapters_completed || 0}/${stats.total_chapters || 15}`, color: 'text-cyan-600', bg: 'bg-cyan-500/10' },
    { icon: Trophy, label: 'Victoires', value: stats.victories_count || 0, color: 'text-yellow-600', bg: 'bg-yellow-500/10' },
    { icon: MessageSquare, label: 'Messages', value: stats.messages_count || 0, color: 'text-violet-600', bg: 'bg-violet-500/10' },
    { icon: Flame, label: 'Série max', value: `${streak?.longest_streak || 0} j`, color: 'text-orange-600', bg: 'bg-orange-500/10' },
  ];
  return (
    <Card className="card-base">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Award className="w-5 h-5 text-primary" />
          Mes stats
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {items.map(({ icon: Icon, label, value, color, bg }) => (
            <div key={label} className={`${bg} rounded-xl p-3 flex flex-col items-center text-center`}>
              <Icon className={`w-6 h-6 ${color} mb-1.5`} />
              <span className="text-2xl font-bold text-foreground">{value}</span>
              <span className="text-xs text-muted-foreground mt-0.5">{label}</span>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <ThumbsUp className="w-3 h-3" /> {stats.victories_supported || 0} félicitations
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Lightbulb className="w-3 h-3" /> {stats.ideas_proposed || 0} idées
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Calendar className="w-3 h-3" /> {stats.days_active || 0} j membre
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/* ============================================================
 * 3. Trophies Grid — all badges (earned + not earned greyed out)
 * ============================================================ */
const TrophiesGrid = ({ badges }) => {
  if (!badges || badges.length === 0) return null;
  const earned = badges.filter(b => b.earned);
  return (
    <Card className="card-base">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Star className="w-5 h-5 text-primary" />
          Mes trophées
          <span className="text-xs font-normal text-muted-foreground ml-1">
            {earned.length}/{badges.length}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <TooltipProvider delayDuration={100}>
          <div className="flex flex-wrap gap-3">
            {badges.map((badge) => (
              <Tooltip key={badge.id}>
                <TooltipTrigger asChild>
                  <div className={badge.earned ? '' : 'opacity-30 grayscale'} data-testid={`badge-${badge.id}`}>
                    <AnimatedBadge badgeId={badge.id} size="md" showAnimation={badge.earned} />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="font-semibold text-sm">{badge.name}</p>
                  <p className="text-xs text-muted-foreground">{badge.description}</p>
                  {!badge.earned && (
                    <p className="text-xs text-orange-500 mt-1 italic">Pas encore débloqué</p>
                  )}
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        </TooltipProvider>
      </CardContent>
    </Card>
  );
};

/* ============================================================
 * 4. Journey Timeline
 * ============================================================ */
const JourneyTimeline = ({ items }) => {
  if (!items || items.length === 0) return null;
  return (
    <Card className="card-base">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Heart className="w-5 h-5 text-primary" />
          Mon parcours
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="relative pl-6 space-y-4 before:content-[''] before:absolute before:left-2 before:top-2 before:bottom-2 before:w-0.5 before:bg-gradient-to-b before:from-primary/40 before:to-primary/5">
          {items.map((item, idx) => {
            const Icon = TIMELINE_ICONS[item.icon];
            return (
              <div key={idx} className="relative" data-testid={`timeline-item-${item.type}`}>
                <div className="absolute -left-6 top-0.5 w-4 h-4 rounded-full bg-primary/20 border-2 border-primary flex items-center justify-center">
                  {Icon ? <Icon className="w-2.5 h-2.5 text-primary" strokeWidth={3} /> : <span className="text-[8px]">{item.icon}</span>}
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{item.title}</p>
                  {item.subtitle && (
                    <p className="text-xs text-muted-foreground">{item.subtitle}</p>
                  )}
                  <p className="text-[10px] text-muted-foreground/70 mt-0.5">{relTime(item.created_at)}</p>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

/* ============================================================
 * Main ProfileShowcase — orchestrates data fetching
 * ============================================================ */
const ProfileShowcase = () => {
  const { api, user } = useAuth();
  const [levelInfo, setLevelInfo] = useState(null);
  const [streak, setStreak] = useState(null);
  const [stats, setStats] = useState(null);
  const [badges, setBadges] = useState([]);
  const [timeline, setTimeline] = useState([]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const [levelR, streakR, statsR, badgesR, timelineR] = await Promise.allSettled([
        api.get('/gamification/my-level'),
        api.get('/gamification/streak'),
        api.get('/users/me/stats'),
        api.get(`/badges/user/${user.id}`),
        api.get('/users/me/timeline?limit=15'),
      ]);
      if (cancelled) return;
      if (levelR.status === 'fulfilled') setLevelInfo(levelR.value.data);
      if (streakR.status === 'fulfilled') setStreak(streakR.value.data);
      if (statsR.status === 'fulfilled') setStats(statsR.value.data);
      if (badgesR.status === 'fulfilled') setBadges(badgesR.value.data);
      if (timelineR.status === 'fulfilled') setTimeline(timelineR.value.data);
    })();
    return () => { cancelled = true; };
  }, [api, user]);

  return (
    <div className="space-y-6" data-testid="profile-showcase">
      <ProgressionCard user={user} levelInfo={levelInfo} streak={streak} />
      <StatsSquares stats={stats} streak={streak} />
      <TrophiesGrid badges={badges} />
      <JourneyTimeline items={timeline} />
    </div>
  );
};

export default ProfileShowcase;
